import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import "./Login.css";
import "../../styles/FormInputs.css";
import blockClubLogo from "../../media/blockclub-logo.png";
import { FiEye, FiEyeOff } from "react-icons/fi";

function Login({ setToken }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [email, setEmail] = useState("");
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const navigate = useNavigate();
  const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:8000";

  const handleKeyPress = (e) => {
    if (e.key === "Enter") handleLogin();
  };

  const handleLogin = async () => {
    setErrorMessage("");
    const token = btoa(`${username}:${password}`);

    try {
      const response = await fetch(`${apiUrl}/auth/login`, {
        method: "POST",
        headers: { Authorization: `Basic ${token}` },
      });

      if (response.status === 403) {
        const err = await response.json().catch(() => ({}));
        console.warn("[Login] Access denied payload:", err);
        navigate("/subscribe", {
          state: {
            redirectAfter: true,
            reason: "no_access",
            message:
              err?.detail ||
              "Your free trial has ended and there are no active trials. Please upgrade to continue.",
          },
        });
        return;
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error("[Login] Non-OK response:", errorData);
        const msg =
          errorData?.detail ||
          "Login unsuccessful. Please check your credentials.";
        setErrorMessage(msg);
        return;
      }

      const data = await response.json();

      // Token
      setToken(data.access_token);
      sessionStorage.setItem("token", data.access_token);

      if (data.pos_update_task_id) {
        sessionStorage.setItem("pos_update_task_id", data.pos_update_task_id);
      }
      if (data.company_id !== undefined) {
        sessionStorage.setItem("company_id", String(data.company_id));
      }
      sessionStorage.setItem("is_paid_user", String(!!data.is_paid_user));
      sessionStorage.setItem(
        "has_active_trials",
        String(!!data.has_active_trials)
      );


      // Access check
      if (data.is_paid_user || data.has_active_trials) {
        navigate("/inventory");
      } else {
        navigate("/subscribe", {
          state: {
            redirectAfter: true,
            reason: "no_access",
            message:
              "Your free trial has ended and there are no active trials. Please upgrade to continue.",
          },
        });
      }
    } catch (error) {
      console.error("[Login] Exception:", error);
      setErrorMessage("Login failed. Please try again.");
    }
  };

  const handlePasswordReset = async () => {
    if (!email) {
      alert("Email cannot be empty.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      alert("Please enter a valid email address.");
      return;
    }

    try {
      const response = await fetch(`${apiUrl}/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);

      const data = await response.json();
      alert(data.message);
      setIsResettingPassword(false);
    } catch {
      alert("Error requesting password reset. Please try again later.");
    }
  };

  return (
    <div className="login-container">
      <div className="login-box">
        <img src={blockClubLogo} alt="Block Club logo" className="block-club-logo" />
        {isResettingPassword ? (
          <>
            <h2 className="login-title">Reset Password</h2>
            <p className="login-subtitle">
              Enter your email to reset your password
            </p>
            <div className="form-group">
              <label className="form-label">Email:</label>
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="standard-input"
              />
            </div>
            <button onClick={handlePasswordReset} className="login-button">
              Send Reset Link
            </button>
            <div className="bottom-text">
              <span
                className="link"
                onClick={() => setIsResettingPassword(false)}
              >
                Back to Login
              </span>
            </div>
          </>
        ) : (
          <>
            <h2 className="login-title">Login</h2>

            <div className="form-group">
              <label className="form-label">Username or Email:</label>
              <input
                type="text"
                placeholder="Username or Email"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                onKeyDown={handleKeyPress}
                className="standard-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Password:</label>
              <div className="standard-password-wrapper">
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={handleKeyPress}
                  className="standard-input"
                />
                {password && (
                  <span
                    className="standard-password-toggle"
                    onClick={() => setShowPassword((prev) => !prev)}
                  >
                    {showPassword ? <FiEyeOff /> : <FiEye />}
                  </span>
                )}
              </div>
            </div>

            {errorMessage && <p className="error-text">{errorMessage}</p>}

            <div className="center-link">
              <span
                className="link"
                onClick={() => setIsResettingPassword(true)}
              >
                Forgot password?
              </span>
            </div>

            <button onClick={handleLogin} className="login-button">
              Login
            </button>

            <div className="bottom-text">
              Don't have an account?{" "}
              <span
                className="link"
                onClick={() => navigate("/signup?service=loyalty_text")}
              >
                Signup
              </span>
            </div>

            <div className="bottom-text bottom-text-with-divider">
              Interested in becoming a venue partner?{" "}
              <span
                className="link"
                onClick={() => window.open('VENUE_SIGNUP_FORM_URL_PLACEHOLDER', '_blank')}
              >
                Sign up here
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default Login;
